import React, { useState } from "react";
import theme from "../../data/theme";

/**
 * TeamNamesEditor — fix a manager's name without starting over.
 *
 * 🔴 LIVE BOARD ONLY, and deliberately so. Sales carry `team` as an index and
 * `userTeam` is an index, so renaming here moves that team's whole history with
 * it. The mock draft is a different story — mockTeamConfigs is keyed by team
 * NAME behind a strict whitelist, so a rename there would silently orphan the
 * seat's archetype, my-guys and keepers. Nothing on this screen touches it.
 */
export default function TeamNamesEditor({ board, onRename, onSetUserTeam, onClose }) {
  const [drafts, setDrafts] = useState(() => [...board.teamNames]);
  const [rejected, setRejected] = useState(-1);

  const commit = (i) => {
    const name = drafts[i].trim();
    if (!name || name === board.teamNames[i]) {
      setDrafts(d => d.map((v, j) => (j === i ? board.teamNames[i] : v)));
      setRejected(-1);
      return;
    }
    if (!onRename(i, name)) {
      // Rejected — duplicate or empty. Put the old name back rather than
      // leaving a value on screen that is not what the board holds.
      setDrafts(d => d.map((v, j) => (j === i ? board.teamNames[i] : v)));
      setRejected(i);
      return;
    }
    setRejected(-1);
  };

  const field = {
    background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
    borderRadius: theme.radius.sm, color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.body, padding: "7px 10px", outline: "none", width: "100%",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: theme.helpers.overlay(0.7), zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div style={{
        background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.radius.xl, padding: 24, width: 460, maxWidth: "100%",
        maxHeight: "85vh", overflowY: "auto",
      }}>
        <div style={{
          fontSize: theme.fontSize.heading, fontWeight: theme.fontWeight.heavy,
          color: theme.colors.text.primary, marginBottom: 4,
        }}>Team names</div>
        <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.muted, marginBottom: 16, lineHeight: 1.5 }}>
          Everything already entered stays with its team. Tap <strong>me</strong> to mark your own seat.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
          {drafts.map((n, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                fontSize: theme.fontSize.micro, color: theme.colors.text.faint, width: 18,
                textAlign: "right", fontVariantNumeric: "tabular-nums",
              }}>{i + 1}</span>
              <input
                style={{ ...field, borderColor: rejected === i ? theme.colors.status.danger : theme.colors.border.default }}
                value={n}
                aria-label={`Team ${i + 1} name`}
                onChange={e => setDrafts(d => d.map((v, j) => (j === i ? e.target.value : v)))}
                onBlur={() => commit(i)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(i); e.target.blur(); } }}
              />
              <button
                type="button"
                onClick={() => onSetUserTeam(i)}
                aria-label={`Mark team ${i + 1} as mine`}
                aria-pressed={board.userTeam === i}
                style={{
                  background: board.userTeam === i ? theme.colors.brand.orange : "transparent",
                  border: `1px solid ${board.userTeam === i ? theme.colors.brand.orange : theme.colors.border.default}`,
                  borderRadius: theme.radius.sm, color: board.userTeam === i ? "#fff" : theme.colors.text.faint,
                  fontFamily: "inherit", fontSize: theme.fontSize.micro, fontWeight: theme.fontWeight.bold,
                  padding: "6px 9px", cursor: "pointer", flexShrink: 0,
                }}
              >me</button>
            </div>
          ))}
        </div>

        {rejected >= 0 && (
          <div style={{ color: theme.colors.status.warning, fontSize: theme.fontSize.caption, marginBottom: 12 }}>
            That name is already taken by another team. The type-ahead could not tell them apart, so it was not changed.
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            background: theme.colors.brand.gradientBtn, border: "none", borderRadius: theme.radius.md,
            color: "#fff", fontFamily: "inherit", fontSize: theme.fontSize.body,
            fontWeight: theme.fontWeight.bold, padding: "9px 18px", cursor: "pointer",
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}
